'use client'
import {
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { Virtualizer, VirtualizerHandle } from 'virtua'

export type GridCell = {
  /** 该 cell 的稳定 key（卡片 id / banner key） */
  key: string
  node: ReactNode
}

type Props = {
  /** 已渲染好的扁平 cell 列表（卡片 + banner cell，按 DOM 顺序） */
  cells: GridCell[]
  /** 当前网格列数；<=0 时退化为不虚拟、整块渲染 */
  columns: number
  /** 真实滚动容器 ref，virtua 监听它的滚动 */
  scrollRef: RefObject<HTMLElement | null> | null
  /** 行内 grid 的列类，需与外部测量列数的容器保持一致 */
  gridClassName: string
  /** 视口外预渲染的像素缓冲，默认 600 */
  bufferSize?: number
  /**
   * 是否还会加载更多。为 true 时，末尾不满一整行的 cell 暂不渲染，
   * 等下一页补齐成整行再显示——避免「最后一行只有一个」的残行
   * （cells 里混入 banner 等，总数往往不是列数整数倍）。
   * 到真正末尾（false）时照常显示，因为那是合法的最后一行。
   */
  hasMore?: boolean
  /**
   * 单行高度估算值（px）。首页卡片高宽比固定（pt-[167.57%]），行高可由
   * useResponsivePageSize 精确算出。传给 virtua 作 itemSize，让它一开始就按真实
   * 行高布局——否则 virtua 先用估算值、图片加载后行高变化触发重测，会让加载新页/
   * 滚动时视口跳位。>0 时才传。
   */
  rowHeight?: number
  /**
   * 行间距（px）。每行是独立 grid，CSS `gap` 只在行内列间生效，行与行之间没有
   * 间距——这里用行 div 的下外边距补回。同时计入 itemSize（见下），保证 virtua
   * 估算的每项高度 = 实际渲染高度（行盒 + 行间距），避免滚动/加载时视口漂移。
   */
  rowGap?: number
  /** 网格之后追加的节点（如 GridLayoutHelper 占位、底部 loading 指示） */
  footer?: ReactNode
  /**
   * 接近列表末尾时回调，用于触发「加载下一页」。由 virtua 直接算出「最后可视行」
   * 的 index（onScroll + findItemIndex），过阈值即回调——不再依赖 DOM 哨兵
   * (IntersectionObserver)。虚拟器是「滚到第几行」的唯一真相源，省掉哨兵在新行
   * 渲染前持续可见导致的连环触发，调用方只需用 loading/hasMore 自然守卫即可。
   * 该回调可能被频繁触发（每次接近末尾的滚动都会调），需自身幂等。
   */
  onReachEnd?: () => void
  /**
   * 触发 onReachEnd 的「距末尾行数」阈值（预取提前量，按行计）。调用方通常传
   * useResponsivePageSize 算出的 prefetchRows（= ceil(视口高 / 行高) ≈ 一屏行数），
   * 让提前量随设备视口/列宽自适应。缺省 4 仅作未测量时的兜底。
   */
  endThreshold?: number
}

/**
 * 按「行」虚拟化的首页网格容器。
 *
 * 把扁平 cell 列表按当前列数切成行，每行作为一个 virtua item，
 * 行内仍用 CSS Grid 布局。Banner 作为普通 cell 计入分组，
 * 不破坏其在扁平序列中的原始位置。
 *
 * columns<=0（尚未测量到列数）时退化为整块渲染，保证首屏不闪烁。
 */
export default function VirtualHomeGrid({
  cells,
  columns,
  scrollRef,
  gridClassName,
  bufferSize = 600,
  hasMore = false,
  rowHeight,
  rowGap = 0,
  footer,
  onReachEnd,
  endThreshold = 4,
}: Props) {
  // 还会加载更多时，把末尾不满一整行的 cell 暂时裁掉（等下一页补齐再显示），
  // 避免 cells 总数非列数整数倍（混入 banner 等）导致「最后一行只有一个」。
  const visibleCells = useMemo(() => {
    if (columns <= 0 || !hasMore) return cells
    const remainder = cells.length % columns
    return remainder === 0 ? cells : cells.slice(0, cells.length - remainder)
  }, [cells, columns, hasMore])

  const rows = useMemo(() => {
    if (columns <= 0) return [visibleCells]
    const result: GridCell[][] = []
    for (let i = 0; i < visibleCells.length; i += columns) {
      result.push(visibleCells.slice(i, i + columns))
    }
    return result
  }, [visibleCells, columns])

  // 末尾预取：问 virtua「视口底边落在第几行」，离最后一行 <= 阈值就回调 onReachEnd。
  // index 由虚拟器同步算出，不受「新行尚未渲染进 DOM」影响，因此不需要哨兵那套
  // 加载闸/上升沿/rAF 补丁——连环触发由调用方的 loading/hasMore 守卫天然挡住。
  const virtualizerRef = useRef<VirtualizerHandle>(null)
  const checkReachEnd = useCallback(() => {
    const handle = virtualizerRef.current
    if (!handle || !onReachEnd || rows.length === 0) return
    const lastVisibleRow = handle.findItemIndex(
      handle.scrollOffset + handle.viewportSize
    )
    if (lastVisibleRow >= rows.length - 1 - endThreshold) onReachEnd()
  }, [onReachEnd, rows.length, endThreshold])

  // 行数变化后「补检」——但只在内容还撑不满视口（不可滚）时才强制补抓一页，直到
  // 出现滚动条。不做无条件预取：首屏约 2 屏内容恰好踩在「离末行 1 屏」的阈值上，
  // 无条件检测会让页面一加载完就立刻预取下一页（用户还没滚就白抓一次）。内容可滚
  // 之后，提前量交给滚动中的 onScroll → checkReachEnd（仍保持满 1 屏预取）。
  useEffect(() => {
    const handle = virtualizerRef.current
    if (!handle || !onReachEnd || rows.length === 0) return
    if (handle.scrollSize <= handle.viewportSize) onReachEnd()
  }, [onReachEnd, rows.length])

  // 列数未测出时退化为整块渲染（不虚拟），但保留按列数的网格布局
  if (columns <= 0) {
    return (
      <>
        <div className={gridClassName}>
          {visibleCells.map((cell) => cell.node)}
        </div>
        {footer}
      </>
    )
  }

  return (
    <>
      <Virtualizer
        ref={virtualizerRef}
        scrollRef={scrollRef ?? undefined}
        bufferSize={bufferSize}
        // 视口底边所在行变化即检测末尾预取（virtua 仅在偏移变化时回调，开销低）
        onScroll={checkReachEnd}
        // itemSize = 行盒高 + 行间距，精确等于每个行 div（含下外边距）的实际占高，
        // 否则 virtua 估算偏差会在滚动/加载时持续修正总高、把视口往上拉。
        itemSize={rowHeight && rowHeight > 0 ? rowHeight + rowGap : undefined}
      >
        {rows.map((row, rowIndex) => (
          <div
            key={row[0]?.key}
            className={gridClassName}
            // 行间距：每行是独立 grid，gap 只在行内列间生效，行与行之间没有间距。
            // 用行 div 的下外边距补回原本由 row-gap 提供的竖向间距（末行不加）。
            style={
              rowIndex < rows.length - 1 && rowGap > 0
                ? { marginBottom: rowGap }
                : undefined
            }
          >
            {row.map((cell) => cell.node)}
          </div>
        ))}
      </Virtualizer>
      {footer}
    </>
  )
}
